import React from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { FeedbackContext, LocalizationContext } from '@cvt/contexts';

import { cacheKeys } from '../config';
import { storyClient } from '../client/storyClient';


export const useStoryCrud = () => {
  const { dictionary } = React.useContext(LocalizationContext);
  const { triggerFeedback, genericErrorFeedback } = React.useContext(FeedbackContext);
  const queryClient = useQueryClient();

  const createStory = useMutation({
    mutationKey: [cacheKeys.createStory],
    mutationFn: storyClient.createStory,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getStories],
      });
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const editStory = useMutation({
    mutationKey: [cacheKeys.editStory],
    mutationFn: ({ id, ...data }: Stories.Edit) => storyClient.editStory({ id, ...data }),
    onSuccess: async (data) => {
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });

      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [cacheKeys.getStories] }),
        queryClient.invalidateQueries({ queryKey: [cacheKeys.getStory, data.data.id] }),
      ]);
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const deleteStory = useMutation({
    mutationKey: [cacheKeys.deleteStory],
    mutationFn: (id: number) => storyClient.deleteStory({ id }),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getStories],
      });
      queryClient.removeQueries({
        queryKey: [cacheKeys.getStory, id],
      });

      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  return {
    createStory: createStory,
    editStory: editStory,
    deleteStory: deleteStory,
  };
};
